import { LTime } from '@app/components/date/LTime';
import { useTooltipWithHotkey } from '@app/core/hotkeys/useTooltipWithHotkey';
import { FragmentIcon } from '@app/modules/fragments/components/FragmentIcon';
import { streamHotkeys } from '@app/modules/logger/screens/stream/stream-hotkeys';
import { toDateTimeStr } from '@mi-front/core';
import { KeyboardTabRoundedLeftIcon, Menu, MenuAction } from '@mi-front/ui';
import {
  Close as CloseIcon,
  Link as LinkIcon,
  PauseRounded as PauseIcon,
  PlayArrowRounded as PlayIcon,
} from '@mui/icons-material';
import KeyboardTabRoundedIcon from '@mui/icons-material/KeyboardTabRounded';
import noop from 'lodash/noop';
import { defineMessage, FormattedMessage, useIntl } from 'react-intl';

export const copyUrlTooltipMessage = defineMessage({
  id: 'logger.streamScreen.copyCursorAt',
  defaultMessage: 'Copiar enlace con marca de tiempo {value}',
});

interface Props {
  readonly loadingAudio: boolean;
  readonly play: null | (() => void);
  readonly pause: null | (() => void);
  readonly startSelection: null | (() => void);
  readonly endSelection: null | (() => void);
  readonly createFragment: null | (() => void);
  readonly copyURL: () => void;
  /** Cursor timestamp (ms) used to show time in the copy-link tooltip. */
  readonly cursorTs?: number;
  readonly clear: () => void;

  // TODO: allow direct react components as well
  readonly customActions?: MenuAction[];
}

export function CursorMenuP(p: Props) {
  const { $t } = useIntl();

  const createFragmentAction: MenuAction = {
    icon: <FragmentIcon fontSize="small" />,
    onClick: p.createFragment || noop,
    tooltip: useTooltipWithHotkey(
      {
        id: 'logger.streamScreen.selectionMenu.createFragment',
        defaultMessage: 'Crear fragmento',
      },
      streamHotkeys.get('selection', 'CREATE_FRAGMENT'),
    ),
    btnProps: {
      'data-testid': 'create-fragment-btn',
    },
  };

  return (
    <Menu
      className="el-cursor-menu"
      actions={[
        {
          icon: <PlayIcon />,
          onClick: p.play || noop,
          tooltip: useTooltipWithHotkey(
            {
              id: 'logger.streamScreen.cursorMenu.play',
              defaultMessage: 'Reproducir desde aquí',
            },
            streamHotkeys.get('playback', 'PLAY_PAUSE'),
          ),
          enabled: !!p.play,
          loading: p.loadingAudio,
        },
        {
          icon: <PauseIcon />,
          onClick: p.pause || noop,
          tooltip: useTooltipWithHotkey(
            {
              id: 'logger.streamScreen.cursorMenu.pause',
              defaultMessage: 'Pausar',
            },
            streamHotkeys.get('playback', 'PLAY_PAUSE'),
          ),
          enabled: !!p.pause,
          loading: p.loadingAudio,
        },
        {
          icon: <KeyboardTabRoundedLeftIcon />,
          onClick: p.startSelection || noop,
          tooltip: useTooltipWithHotkey(
            {
              id: 'logger.streamScreen.cursorMenu.startSelection',
              defaultMessage: 'Iniciar selección aquí',
            },
            streamHotkeys.get('selection', 'START_CURSOR_SELECTION'),
          ),
          enabled: true,
          btnProps: {
            disabled: !p.startSelection,
          },
        },
        {
          icon: <KeyboardTabRoundedIcon />,
          onClick: p.endSelection || noop,
          tooltip: useTooltipWithHotkey(
            {
              id: 'logger.streamScreen.cursorMenu.endSelection',
              defaultMessage: 'Finalizar selección aquí',
            },
            streamHotkeys.get('selection', 'END_CURSOR_SELECTION'),
          ),
          enabled: true,
          btnProps: {
            disabled: !p.endSelection,
          },
        },
        ...(p.createFragment ? [createFragmentAction] : []),
        {
          icon: <LinkIcon fontSize="small" />,
          onClick: p.copyURL,
          tooltip:
            p.cursorTs != null ? (
              <FormattedMessage
                {...copyUrlTooltipMessage}
                values={{
                  value: <LTime value={toDateTimeStr(p.cursorTs)} hour12={false} showSeconds />,
                }}
              />
            ) : (
              $t(copyUrlTooltipMessage, { value: '' })
            ),
        },
        ...(p.customActions || []),
        {
          icon: <CloseIcon fontSize="small" />,
          onClick: p.clear,
          tooltip: $t({
            id: 'logger.streamScreen.cursorMenu.cancel',
            defaultMessage: 'Cancelar',
          }),
        },
      ]}
    />
  );
}
